import { CommonModule } from '@angular/common';
import { EzPanelComponent } from '../components/ez-panel/panel/panel.component';
import { EzStandardStateComponent } from '../components/ez-standard-state/ez-standard-state.component';
import { UiButtonDirective } from '../directives/ui-button.directive';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  Type,
  ViewChild,
  EventEmitter,
  Output,
  inject,
} from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { BlockRegistry, parseBlockSettings } from './block-registry';
import { BlockRuntimeLease } from './block-runtime';
import { WorkspaceBlockOutletDirective } from './workspace-block-outlet.directive';
import type {
  BlockEnvironment,
  BlockHost,
  BlockReference,
  BlockRegion,
  WorkspaceBlock,
} from './block-contract';
import { TWO_COLUMN_WORKSPACE } from './block-contract';
import { WorkspaceContextSession } from './context-reads';
import type { PanelSkeletonConfig } from '../types/panel.types';

export interface BlockPanelLoadingPresentation {
  readonly skeleton: PanelSkeletonConfig;
  readonly subtitle?: string;
}

interface MountedBlock {
  instance: WorkspaceBlock;
  name: string;
  columns: number;
  minimumHeight: number;
  component?: Type<unknown>;
  lease?: BlockRuntimeLease;
  error?: string;
}

@Component({
  selector: 'ez-workspace-block-host',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    EzPanelComponent,
    EzStandardStateComponent,
    UiButtonDirective,
    WorkspaceBlockOutletDirective,
  ],
  template: ` <div class="workspace">
      <div class="workspace__summary">
        <ng-content select="[workspaceSummary]" />
      </div>
      <div class="workspace__actions">
        <ng-content select="[workspaceActions]" />
      </div>
      <div class="workspace__requirements">
        <ng-content select="[workspaceRequirements]" />
      </div>
      @if (diagnostics.length) {
        <div class="workspace__issues" role="alert">
          <strong>Workspace unavailable</strong>
          <ul>
            @for (message of diagnostics; track $index) {
              <li>{{ message }}</li>
            }
          </ul>
        </div>
      }
      <div
        class="workspace__body"
        [class.workspace__body--supporting]="
          hasSupporting && authoringRegion !== 'supporting'
        "
      >
        <div
          #main
          class="workspace__region"
          aria-label="Main workspace"
          [hidden]="authoringRegion === 'supporting'"
          cdkDropList
          cdkDropListOrientation="mixed"
          [id]="regionDropId('main')"
          [cdkDropListData]="mainBlocks"
          [cdkDropListConnectedTo]="connections('main')"
          [cdkDropListDisabled]="!authoring || !allowedRegions.includes('main')"
          (cdkDropListDropped)="drop($event, 'main')"
          [class.workspace__region--editable]="
            authoring && allowedRegions.includes('main')
          "
        >
          @if (
            authoring &&
            authoringRegion !== 'supporting' &&
            allowedRegions.includes('main') &&
            !mainBlocks.length
          ) {
            @if (authoringEmptyTemplate) {
              <ng-container
                *ngTemplateOutlet="
                  authoringEmptyTemplate;
                  context: { $implicit: 'main' }
                "
              />
            } @else {
              <span class="workspace__drop-hint">Main panels</span>
            }
          }
          @for (block of mainBlocks; track block.instance.instanceId) {
            <section
              class="workspace__panel"
              cdkDrag
              [cdkDragData]="block.instance"
              [cdkDragDisabled]="
                !authoring || block.instance === mainInteraction
              "
              [class.workspace__panel--editable]="authoring"
              [class.workspace__panel--library]="!authoring"
              [class.workspace__panel--selected]="
                authoring && selectedInstanceId === block.instance.instanceId
              "
              (click)="select(block.instance.instanceId)"
              [hidden]="diagnostics.length > 0 || !onActiveTab(block.instance)"
              [attr.inert]="!onActiveTab(block.instance) ? '' : null"
              [attr.aria-label]="block.name"
              [attr.data-block-instance]="block.instance.instanceId"
              [style.grid-column]="'span ' + block.columns"
              [style.min-height.px]="authoring ? block.minimumHeight : null"
            >
              @if (authoring && block.instance !== mainInteraction) {
                <div class="workspace__panel-heading">
                  <button
                    uiButton
                    type="button"
                    cdkDragHandle
                    class="workspace__grip"
                    [text]="true"
                    [outlined]="false"
                    [attr.aria-label]="'Move ' + block.name"
                    title="Drag to move panel"
                  >
                    <i class="pi pi-bars" aria-hidden="true"></i>
                  </button>
                  <button
                    uiButton
                    type="button"
                    class="workspace__select"
                    [text]="true"
                    [outlined]="false"
                    [wrap]="true"
                    [attr.aria-label]="'Configure ' + block.name"
                    [attr.aria-pressed]="
                      selectedInstanceId === block.instance.instanceId
                    "
                  >
                    <strong>{{ block.name }}</strong
                    ><i class="pi pi-pencil" aria-hidden="true"></i>
                  </button>
                  @if (block.lease?.panel?.actionsTemplate(); as actions) {
                    <span class="workspace__preview-actions" inert (click)="$event.stopPropagation()">
                      <ng-container [ngTemplateOutlet]="actions" />
                    </span>
                  }
                </div>
              }
              @if (block.instance !== mainInteraction) {
                @let loading = authoring ? undefined : panelLoadingPresentation?.(block.instance.block);
                <ui-panel [headerTemplate]="authoring ? emptyPanelHeader : undefined" [panelTitle]="block.name" [totalCount]="block.lease?.panel?.totalCount()"
                  [subtitle]="block.lease?.panel?.subtitle() ?? loading?.subtitle" [headerActionsTemplate]="block.lease?.panel?.actionsTemplate?.()"
                  [loading]="isPanelLoading(block, loading)" [skeletonConfig]="loading?.skeleton">
                  <div class="workspace__panel-content" [class.workspace__sample]="authoring" [attr.inert]="authoring ? '' : null"
                    [style.min-height]="loading ? loading.skeleton.minHeight : null">
                    <ng-container *ngTemplateOutlet="panelContent; context: { $implicit: block }" />
                  </div>
                </ui-panel>
              } @else {
              <div
                [class.workspace__sample]="authoring"
                [attr.inert]="authoring ? '' : null"
              >
                <ng-container
                  *ngTemplateOutlet="
                    panelContent;
                    context: { $implicit: block }
                  "
                />
              </div>
              }
            </section>
          }
        </div>
        <div
          #supporting
          class="workspace__region"
          aria-label="Supporting information"
          [hidden]="!hasSupporting"
          cdkDropList
          cdkDropListOrientation="mixed"
          [id]="regionDropId('supporting')"
          [cdkDropListData]="supportingBlocks"
          [cdkDropListConnectedTo]="connections('supporting')"
          [cdkDropListDisabled]="
            !authoring || !allowedRegions.includes('supporting')
          "
          (cdkDropListDropped)="drop($event, 'supporting')"
          [class.workspace__region--editable]="
            authoring && allowedRegions.includes('supporting')
          "
        >
          @if (
            authoring &&
            authoringRegion !== 'main' &&
            allowedRegions.includes('supporting') &&
            !supportingBlocks.length
          ) {
            @if (authoringEmptyTemplate) {
              <ng-container
                *ngTemplateOutlet="
                  authoringEmptyTemplate;
                  context: { $implicit: 'supporting' }
                "
              />
            } @else {
              <span class="workspace__drop-hint">Supporting panels</span>
            }
          }
          @for (block of supportingBlocks; track block.instance.instanceId) {
            <section
              class="workspace__panel"
              cdkDrag
              [cdkDragData]="block.instance"
              [cdkDragDisabled]="!authoring"
              [class.workspace__panel--editable]="authoring"
              [class.workspace__panel--library]="!authoring"
              [class.workspace__panel--selected]="
                authoring && selectedInstanceId === block.instance.instanceId
              "
              (click)="select(block.instance.instanceId)"
              [hidden]="diagnostics.length > 0"
              [attr.aria-label]="block.name"
              [attr.data-block-instance]="block.instance.instanceId"
              [style.grid-column]="'span ' + block.columns"
              [style.min-height.px]="authoring ? block.minimumHeight : null"
            >
              @if (authoring) {
                <div class="workspace__panel-heading">
                  <button
                    uiButton
                    type="button"
                    cdkDragHandle
                    class="workspace__grip"
                    [text]="true"
                    [outlined]="false"
                    [attr.aria-label]="'Move ' + block.name"
                    title="Drag to move panel"
                  >
                    <i class="pi pi-bars" aria-hidden="true"></i>
                  </button>
                  <button
                    uiButton
                    type="button"
                    class="workspace__select"
                    [text]="true"
                    [outlined]="false"
                    [wrap]="true"
                    [attr.aria-label]="'Configure ' + block.name"
                    [attr.aria-pressed]="
                      selectedInstanceId === block.instance.instanceId
                    "
                  >
                    <strong>{{ block.name }}</strong
                    ><i class="pi pi-pencil" aria-hidden="true"></i>
                  </button>
                  @if (block.lease?.panel?.actionsTemplate(); as actions) {
                    <span class="workspace__preview-actions" inert (click)="$event.stopPropagation()">
                      <ng-container [ngTemplateOutlet]="actions" />
                    </span>
                  }
                </div>
              }
              @if (block.instance !== mainInteraction) {
                @let loading = authoring ? undefined : panelLoadingPresentation?.(block.instance.block);
                <ui-panel [headerTemplate]="authoring ? emptyPanelHeader : undefined" [panelTitle]="block.name" [totalCount]="block.lease?.panel?.totalCount()"
                  [subtitle]="block.lease?.panel?.subtitle() ?? loading?.subtitle" [headerActionsTemplate]="block.lease?.panel?.actionsTemplate?.()"
                  [loading]="isPanelLoading(block, loading)" [skeletonConfig]="loading?.skeleton">
                  <div class="workspace__panel-content" [class.workspace__sample]="authoring" [attr.inert]="authoring ? '' : null"
                    [style.min-height]="loading ? loading.skeleton.minHeight : null">
                    <ng-container *ngTemplateOutlet="panelContent; context: { $implicit: block }" />
                  </div>
                </ui-panel>
              } @else {
              <div
                [class.workspace__sample]="authoring"
                [attr.inert]="authoring ? '' : null"
              >
                <ng-container
                  *ngTemplateOutlet="
                    panelContent;
                    context: { $implicit: block }
                  "
                />
              </div>
              }
            </section>
          }
        </div>
      </div>
      @if (
        !authoring && !blocks.length && !mainInteraction && !diagnostics.length
      ) {
        <p class="workspace__empty">
          No panels are configured for this workspace.
        </p>
      }
    </div>
    <ng-template #emptyPanelHeader />
    <ng-template #panelContent let-block>
      @if (block.error) {
        <ui-standard-state
          kind="error"
          [message]="block.error"
          [recoveryLabel]="'Retry ' + block.name"
          (recovery)="retry(block)"
        />
      } @else if (block.component) {
        <ng-container
          [workspaceBlockOutlet]="block.component" [blockRuntime]="block.lease"
        />
      } @else {
        <p role="status">Loading {{ block.name }}…</p>
      }
    </ng-template>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        container-type: inline-size;
      }
      .workspace {
        min-width: 0;
      }
      .workspace__summary,
      .workspace__actions,
      .workspace__requirements {
        min-width: 0;
      }
      .workspace__body {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: var(--workspace-gap, 10px);
      }
      .workspace__region {
        display: grid;
        grid-template-columns: repeat(12, minmax(0, 1fr));
        gap: var(--workspace-gap, 10px);
        align-content: start;
        min-width: 0;
      }
      .workspace__region[hidden],
      .workspace__panel[hidden] {
        display: none;
      }
      .workspace__panel {
        min-width: 0;
        background: var(--ui-panel-background-color, Canvas);
        color: var(--ui-workspace-text-color, CanvasText);
        padding: 16px;
        border: 1px solid var(--ui-border-subtle-color, ButtonBorder);
        border-radius: 6px;
        overflow-wrap: anywhere;
      }
      h3 {
        margin: 0 0 12px;
        font-size: 1rem;
      }
      .workspace__issues {
        padding: 12px;
        border: 1px solid var(--ui-workspace-error-border-color, ButtonBorder);
        background: var(--ui-workspace-error-background-color, Canvas);
        color: var(--ui-text-color-error, CanvasText);
        overflow-wrap: anywhere;
      }
      .workspace__issues ul {
        margin: 8px 0;
        padding-left: 20px;
      }
      .workspace__region--editable {
        min-height: 3rem;
        padding: 3px;
        border: 1px dashed var(--ui-border-subtle-color, ButtonBorder);
        border-radius: 5px;
      }
      .workspace__drop-hint {
        grid-column: span 12;
        font-size: 0.75rem;
        color: var(--ui-text-muted, GrayText);
        align-self: center;
        padding: 0.5rem;
      }
      .workspace__panel--library { padding:0; border:0; background:transparent; }
      .workspace__panel--library ui-panel { display:block; min-width:0; }
      .workspace__panel-content { padding:var(--workspace-panel-padding, .5rem .625rem); min-width:0; }
      .workspace__panel--editable {
        cursor: pointer;
        background: var(--ui-panel-background-color, Canvas);
      }
      .workspace__panel--editable:hover,
      .workspace__panel--selected {
        border-color: var(--ui-workspace-selected-border-color, Highlight);
      }
      .workspace__panel--selected {
        background: var(--ui-workspace-selected-background-color, Highlight);
        color: var(--ui-workspace-selected-text-color, HighlightText);
        box-shadow: 0 0 0 1px var(--ui-workspace-selected-border-color, Highlight);
      }
      .workspace__panel-heading {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.4rem;
        margin-bottom: 0.7rem;
      }
      .workspace__grip,
      .workspace__select {
        --ui-button-horizontal-padding-in-grid: 5;
        --ui-button-vertical-padding-small: 5;
      }
      .workspace__select {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        flex: 1 1 12rem;
        min-width: min(12rem, 100%);
        text-align: left;
        cursor: pointer;
      }
      .workspace__grip {
        cursor: grab;
      }
      .workspace__sample {
        pointer-events: none;
      }
      .cdk-drag-preview {
        box-sizing: border-box;
        box-shadow: var(--ui-workspace-drag-shadow, 0 4px 12px rgb(0 0 0 / 0.2));
        color: var(--ui-workspace-text-color, CanvasText);
      }
      .cdk-drag-placeholder {
        opacity: 0.25;
      }
      @container (min-width:1200px) {
        .workspace__body--supporting {
          grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
        }
      }
    `,
  ],
})
export class WorkspaceBlockHostComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input({ required: true }) registry!: BlockRegistry;
  @Input({ required: true }) session!: WorkspaceContextSession;
  @Input({ required: true }) host!: BlockHost;
  @Input() blocks: readonly WorkspaceBlock[] = [];
  @Input() panelLoadingPresentation?: (
    block: BlockReference,
  ) => BlockPanelLoadingPresentation | undefined;
  @Input() mainInteraction: WorkspaceBlock | null = null;
  /** Visibility only: changing tabs must never dispose a mounted block or its lease. */
  @Input() activeTabId: string | null = null;
  onActiveTab(block: WorkspaceBlock): boolean {
    return this.activeTabId === null || block.placement.region !== 'main' || block.tabId === this.activeTabId;
  }
  @Input() authoring = false;
  @Input() selectedInstanceId: string | null = null;
  @Input() authoringRegion: BlockRegion | null = null;
  @Input() allowedRegions: readonly BlockRegion[] = ['main', 'supporting'];
  @Input() authoringGroup = '';
  @Input() authoringEmptyTemplate: TemplateRef<{
    $implicit: BlockRegion;
  }> | null = null;
  @Output() blockConfigure = new EventEmitter<string>();
  @Output() blockMove = new EventEmitter<{
    id: string;
    region: BlockRegion;
    index: number;
  }>();
  private static nextId = 0;
  private readonly localDropId = `workspace-blocks-${++WorkspaceBlockHostComponent.nextId}`;
  dropId(region: BlockRegion): string {
    return `${this.authoringGroup || this.localDropId}-${region}`;
  }
  regionDropId(region: BlockRegion): string {
    return this.authoringRegion && this.authoringRegion !== region
      ? `${this.localDropId}-${region}`
      : this.dropId(region);
  }
  connections(region: BlockRegion): string[] {
    return this.authoring && this.authoringGroup
      ? this.allowedRegions
          .filter((item) => item !== region)
          .map((item) => this.dropId(item))
      : [];
  }
  select(id: string): void {
    if (this.authoring && this.blocks.some((block) => block.instanceId === id))
      this.blockConfigure.emit(id);
  }
  drop(
    event: CdkDragDrop<MountedBlock[], MountedBlock[], WorkspaceBlock>,
    region: BlockRegion,
  ): void {
    const block = event.item.data;
    if (
      !this.authoring ||
      !this.allowedRegions.includes(region) ||
      !event.isPointerOverContainer ||
      !this.allowedRegions.some(
        (item) => event.previousContainer.id === this.dropId(item),
      ) ||
      event.container.id !== this.dropId(region) ||
      !block?.instanceId ||
      !this.blocks.some((item) => item.instanceId === block.instanceId) ||
      !Number.isInteger(event.currentIndex) ||
      !event.previousContainer.data.some(
        (item) => item.instance.instanceId === block.instanceId,
      ) ||
      event.currentIndex < 0 ||
      event.currentIndex >
        event.container.data.length -
          (event.previousContainer === event.container ? 1 : 0) ||
      (event.previousContainer === event.container &&
        event.previousIndex === event.currentIndex)
    )
      return;
    try {
      const manifest = this.registry.resolve(block.block);
      if (
        !manifest.placements.some(
          (placement) =>
            placement.host === this.host && placement.region === region,
        )
      )
        return;
    } catch {
      return;
    }
    this.blockMove.emit({
      id: block.instanceId,
      region,
      index: Math.max(
        0,
        event.currentIndex - (region === 'main' && this.mainInteraction ? 1 : 0),
      ),
    });
  }
  @ViewChild('main', { static: true }) main!: ElementRef<HTMLElement>;
  @ViewChild('supporting', { static: true })
  supporting!: ElementRef<HTMLElement>;
  diagnostics: string[] = [];
  mainBlocks: MountedBlock[] = [];
  supportingBlocks: MountedBlock[] = [];
  hasSupporting = false;
  private observer?: ResizeObserver;
  private initialized = false;
  private destroyed = false;
  private closed = false;
  private generation = 0;
  private signature = '';
  private unsubscribe?: () => void;
  private environment?: BlockEnvironment;
  private effective: readonly WorkspaceBlock[] = [];
  private readonly changes = inject(ChangeDetectorRef);

  ngAfterViewInit(): void {
    this.initialized = true;
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.reconcile());
      this.observer.observe(this.main.nativeElement);
      this.observer.observe(this.supporting.nativeElement);
    }
    // After the first layout, region measurements include shell rails and padding.
    queueMicrotask(() => {
      if (!this.destroyed) this.reconcile();
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['session']) {
      this.closed = false;
      this.unsubscribe?.();
      this.unsubscribe = this.session?.onInvalidated((reason) => {
        if (
          reason === 'context-replaced' ||
          reason === 'disposed' ||
          reason === 'access-changed'
        ) {
          this.clear();
          this.signature = '';
          if (reason !== 'disposed') this.reconcile();
          else {
            this.closed = true;
            this.diagnostics = ['Workspace context is closed.'];
            this.changes.markForCheck();
          }
        }
      });
    }
    if (
      ![
        'session',
        'registry',
        'host',
        'blocks',
        'mainInteraction',
        'authoringRegion',
      ].some((key) => changes[key])
    )
      return;
    this.signature = '';
    this.clear();
    this.hasSupporting =
      this.authoringRegion === 'supporting' ||
      this.blocks.some((b) => b.placement.region === 'supporting');
    if (this.initialized)
      queueMicrotask(() => {
        if (!this.destroyed) this.reconcile();
      });
  }
  private clear(): void {
    this.generation++;
    for (const block of [...this.mainBlocks, ...this.supportingBlocks]) {
      block.lease?.dispose();
    }
    this.mainBlocks = [];
    this.supportingBlocks = [];
  }
  private reconcile(): void {
    if (
      !this.initialized ||
      this.destroyed ||
      this.closed ||
      !this.registry ||
      !this.session
    )
      return;
    const widths = {
      main: this.main.nativeElement.getBoundingClientRect().width,
      supporting: this.supporting.nativeElement.getBoundingClientRect().width,
    };
    if (
      (this.authoringRegion === 'supporting'
        ? widths.supporting
        : widths.main) <= 0
    )
      return; // A collapsed dashboard expansion has not acquired a layout yet.
    const context = this.session.context;
    this.environment = {
      host: this.host,
      layoutProfile: TWO_COLUMN_WORKSPACE,
      regionWidths: widths,
      contextKeys: [
        ...Object.keys(context.domain),
        ...(context.workflow
          ? [
              'workflowInstanceId',
              ...(context.workflow.stepOccurrenceId
                ? ['stepOccurrenceId']
                : []),
            ]
          : []),
      ] as BlockEnvironment['contextKeys'],
    };
    this.effective = this.mainInteraction
      ? [this.mainInteraction, ...this.blocks]
      : this.blocks;
    const issues =
      this.mainInteraction &&
      (this.mainInteraction.placement.region !== 'main' ||
        this.mainInteraction.width !== 'full')
        ? [
            {
              instanceId: this.mainInteraction.instanceId,
              code: 'custom-main',
              message:
                'A custom main interaction must be a registered full-width main panel.',
            },
          ]
        : this.registry.validate(this.effective, this.environment);
    this.diagnostics = issues.map((d) => `${d.instanceId}: ${d.message}`);
    if (issues.length) {
      // Hide already mounted panels at insufficient widths, retaining unsaved inputs until they fit again.
      // Context/contract failures destroy leases so stale or incompatible content cannot survive.
      if (issues.some((d) => d.code !== 'minimum-size')) {
        this.clear();
        this.signature = '';
      }
      this.changes.markForCheck();
      return;
    }
    const layout = this.registry.layout(this.effective, this.environment);
    // Width changes only adjust spans; they never destroy an editor or its unsaved values.
    const signature = JSON.stringify([this.effective, context]);
    if (signature === this.signature) {
      for (const block of [...this.mainBlocks, ...this.supportingBlocks])
        block.columns = layout.find(
          (l) => l.instanceId === block.instance.instanceId,
        )!.columns;
    } else {
      this.clear();
      this.signature = signature;
      const mounted = this.effective.map((instance) => ({
        instance,
        name: this.registry.resolve(instance.block).name,
        columns: layout.find((l) => l.instanceId === instance.instanceId)!
          .columns,
        minimumHeight: layout.find((l) => l.instanceId === instance.instanceId)!
          .minimumHeightPx,
      }));
      this.mainBlocks = mounted.filter(
        (b) => b.instance.placement.region === 'main',
      );
      this.supportingBlocks = mounted.filter(
        (b) => b.instance.placement.region === 'supporting',
      );
      mounted.forEach((block) => {
        void this.load(block);
      });
    }
    this.changes.markForCheck();
  }
  retry(block: MountedBlock): void {
    block.error = undefined;
    void this.load(block);
  }
  isPanelLoading(block: MountedBlock, presentation?: BlockPanelLoadingPresentation): boolean {
    return !!presentation && !block.error &&
      (!block.component || block.lease?.panel.loading() === true);
  }
  private async load(block: MountedBlock): Promise<void> {
    const generation = this.generation;
    try {
      const component = await this.registry.load(
        block.instance.instanceId,
        this.effective,
        this.environment!,
      );
      if (generation !== this.generation || this.destroyed) return;
      const manifest = this.registry.resolve(block.instance.block);
      const settings = parseBlockSettings(
        manifest.settingsSchema,
        block.instance.settings,
      );
      block.lease = new BlockRuntimeLease(manifest, settings, this.session);
      block.component = component;
    } catch (error) {
      if (generation !== this.generation || this.destroyed) return;
      block.error =
        error instanceof Error ? error.message : 'Unable to load this panel.';
    }
    this.changes.markForCheck();
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.observer?.disconnect();
    this.unsubscribe?.();
    this.clear();
  }
}
